// src/data/leaderPreviewData.ts
// All fixture data for the standalone leader preview section.
// No imports from hook files — plain TypeScript objects only.

// ─── Shared ───────────────────────────────────────────────────────────────────

export const PREVIEW_LEADER_NAME = "Alex Rivera";
export const PREVIEW_LEADER_INITIALS = "AR";
export const PREVIEW_BEHAVIOR_NAME = "Strategic Listening";
export const PREVIEW_COACH_NAME = "Jordan Lee";
export const PREVIEW_COACH_USER_ID = "preview-coach-user";

// ─── Today Page ───────────────────────────────────────────────────────────────

export const PREVIEW_TODAY = {
  streak: 7,
  avgPracticeFrequency: "3x / week",
  behaviorName: PREVIEW_BEHAVIOR_NAME,
  behaviorPhase: "Active Practice",
  progressPercent: 62,
  behavioralObservation: {
    moment: "In your recent practice, Strategic Listening is the clearest behavior currently in view.",
    pattern: "This is supported by 3 meetings where you paused before responding and asked clarifying questions.",
    reflection: "One thing to carry into today: try summarizing what you heard before offering your perspective.",
  },
  meetings: [
    { id: "m1", title: "Product Roadmap Sync", formattedTime: "10:00 – 11:00 AM", isSelected: true, isToday: true, hasReflection: false },
    { id: "m2", title: "1:1 with Sarah Chen", formattedTime: "1:00 – 1:30 PM", isSelected: true, isToday: true, hasReflection: false },
    { id: "m3", title: "Quarterly Planning", formattedTime: "3:00 – 4:30 PM", isSelected: false, isToday: true, hasReflection: false },
  ],
  continuityThisWeek: [
    "You've practiced Strategic Listening in 2 of 3 selected meetings this week.",
    "Longest streak to date: 9 days.",
    "Your coach noted improvement in meeting preparation last session.",
  ],
};

// ─── Weekly Schedule Page ─────────────────────────────────────────────────────

export const PREVIEW_SCHEDULE = {
  weekRange: "Mar 30 – Apr 5, 2026",
  statsSelected: 4,
  statsReflections: 3,
  statsMeetings: 7,
  behavioralObservation: {
    moment: "In your Stakeholder Update, Strategic Listening showed up in a meeting you selected for practice this week.",
    pattern: "This is supported by a saved reflection and transcript evidence from the same meeting.",
    reflection: null as string | null,
  },
  // Calendar grid: one entry per day of the week (Mon–Sun)
  calendarDays: [
    {
      label: "MON",
      date: 30,
      isToday: true,
      meetings: [
        { id: "w1", title: "Team Standup", time: "9:00 AM", isSelected: true, hasReflection: true },
        { id: "w2", title: "Design Review", time: "2:00 PM", isSelected: false, hasReflection: false },
      ],
    },
    {
      label: "TUE",
      date: 31,
      isToday: false,
      meetings: [
        { id: "w3", title: "Stakeholder Update", time: "11:00 AM", isSelected: true, hasReflection: true },
      ],
    },
    {
      label: "WED",
      date: 1,
      isToday: false,
      meetings: [
        { id: "w4", title: "Product Roadmap Sync", time: "10:00 AM", isSelected: true, hasReflection: false },
        { id: "w5", title: "1:1 with Sarah Chen", time: "1:00 PM", isSelected: true, hasReflection: false },
      ],
    },
    {
      label: "THU",
      date: 2,
      isToday: false,
      meetings: [
        { id: "w6", title: "Leadership Forum", time: "3:00 PM", isSelected: false, hasReflection: false },
      ],
    },
    { label: "FRI", date: 3, isToday: false, meetings: [] },
    { label: "SAT", date: 4, isToday: false, meetings: [] },
    { label: "SUN", date: 5, isToday: false, meetings: [] },
  ],
};

// ─── Insights Page ────────────────────────────────────────────────────────────

export const PREVIEW_INSIGHTS = {
  // Hero section
  heroBadge: "Active Practice",
  heroText: "Your recent meetings are showing a more complex pattern.",
  heroSubtext: "Strategic Listening is standing out right now, with some signs of support and counter-pressure appearing across recent conversations.",
  // Living Constellation quadrants
  constellation: [
    { quadrant: "CONNECT", dotCount: 3, trend: "up" as const },
    { quadrant: "OWN", dotCount: 2, trend: "stable" as const },
    { quadrant: "RESPOND", dotCount: 4, trend: "up" as const },
    { quadrant: "ELEVATE", dotCount: 1, trend: "stable" as const },
  ],
  // Learning timeline
  learningTimeline: [
    {
      id: "lt1",
      meetingTitle: "Stakeholder Update",
      type: "transcript" as const,
      badge: "Transcript Analysis",
      marker: "Clear Next Steps",
      date: "Mar 28, 2026",
      insight: "You naturally ground coaching in observable moments and experiments, which lifts your coaching philosophy. Combining deliberate space for the client to speak, co-design solutions, and direct moments in-session will make that approach more generative and practice-oriented.",
    },
    {
      id: "lt2",
      meetingTitle: "Team Standup",
      type: "transcript" as const,
      badge: "Transcript Analysis",
      marker: "Clear Next Steps",
      date: "Mar 26, 2026",
      insight: "Directness is clear and valuable here, but to make sessions actionable you need to couple them with nested scenes, deliberation, and short follow-up cadence so the leader knows what to do next and who is accountable.",
    },
    {
      id: "lt3",
      meetingTitle: "Design Review",
      type: "transcript" as const,
      badge: "Transcript Analysis",
      marker: "Clear Next Steps",
      date: "Mar 24, 2026",
      insight: "Noticing the next steps proactively can strengthen clarity and narrative, ensuring all participants feel confident in the tasks ahead.",
    },
  ],
  // Behavior map
  behaviorMap: [
    { quadrant: "CONNECT", dotCount: 3, description: "3 behavior signals · Appears in 2 transcripts" },
    { quadrant: "OWN", dotCount: 2, description: "2 behavior signals · Appears in 2 transcripts" },
    { quadrant: "RESPOND", dotCount: 4, description: "4 behavior signals · Appears in 3 transcripts" },
    { quadrant: "ELEVATE", dotCount: 1, description: "1 behavior signal · Appears in 1 transcript" },
  ],
  // Transcript trends
  transcriptTrends: {
    dailySummary: "Across 3 meetings this week, we're noticing 4 transcript signals and 3 behavioral coach signals.\nAcross 3 meetings: 3 transcript signals, 3 reflection-based signals.",
    uploadFrequency: "Your transcript uploads are becoming more frequent.",
    analysisCompletion: "More of your transcripts are completing analysis.",
    updatedAt: "Mar 30, 2026",
  },
};

export type PreviewTranscriptAnalysisFixture = {
  id: string;
  meetingTitle: string;
  occurredAt: string;
  scrubbedTranscript: string;
  analysisVersion: number;
  overallAssessment: string;
  summaryText: string;
  keyInsight: string;
  nextExperiment: string;
  encouragement: string;
  whyThisMatters: string;
  strengthsObserved: Array<{
    quote: string;
    what_worked: string;
    impact: string;
  }>;
  growthEdges: Array<{
    quote: string;
    moment: string;
    alternative_approach: string;
    micro_action: string;
  }>;
};

export const PREVIEW_TRANSCRIPT_ANALYSES: PreviewTranscriptAnalysisFixture[] = [
  {
    id: "lt1",
    meetingTitle: "Design Review",
    occurredAt: "2026-07-02T16:00:00.000Z",
    scrubbedTranscript: [
      "00:04:12 Participant 1: We still need a direction.",
      "00:04:28 Leader: What evidence are others seeing?",
      "00:04:41 Participant 2: Customer feedback favors the simpler option.",
      "00:05:06 Leader: I want to hear from someone who has not spoken yet.",
      "00:05:22 Participant 3: The implementation risk is lower if we stage the change.",
    ].join("\n"),
    analysisVersion: 1,
    overallAssessment: "You created space for the team to surface evidence before naming a direction.",
    summaryText: "The discussion moved toward a decision, and your strongest moments came when you asked for evidence and invited a quieter participant into the read.",
    keyInsight: "Creating space before deciding helped the team compare options without treating the first view as final.",
    nextExperiment: "Before offering your recommendation, ask one person what evidence they are seeing and one person what could disconfirm it.",
    encouragement: "This is a useful leadership pattern because it keeps the decision grounded in shared evidence.",
    whyThisMatters: "Inviting evidence before direction can help the group own the decision instead of simply complying with it.",
    strengthsObserved: [
      {
        quote: "What evidence are others seeing?",
        what_worked: "You asked for evidence before offering your own recommendation.",
        impact: "The group had a chance to make the reasoning visible.",
      },
      {
        quote: "I want to hear from someone who has not spoken yet.",
        what_worked: "You invited a quieter participant into the decision.",
        impact: "The decision had a broader source base before the team moved on.",
      },
    ],
    growthEdges: [
      {
        quote: "We still need a direction.",
        moment: "The room was pushing for closure before the evidence had been fully compared.",
        alternative_approach: "Name the decision pressure, then ask what evidence would make the direction safe enough.",
        micro_action: "Say, 'I hear the need for a direction. What evidence would make this safe enough to decide?'",
      },
    ],
  },
  {
    id: "lt2",
    meetingTitle: "Executive Forecast",
    occurredAt: "2026-07-07T16:00:00.000Z",
    scrubbedTranscript: [
      "00:10:03 Participant 1: The team has not responded yet.",
      "00:10:17 Leader: We need to choose the direction now.",
      "00:10:31 Participant 2: I still have one concern.",
      "00:10:48 Leader: We can return to that after the forecast is committed.",
    ].join("\n"),
    analysisVersion: 1,
    overallAssessment: "You gave the meeting needed direction, while the recording also shows a moment where decision pressure narrowed the room too quickly.",
    summaryText: "The forecast conversation reached closure, but one concern remained outside the decision frame.",
    keyInsight: "The leadership move is not to avoid decisions, but to make the tradeoff visible before closing the room.",
    nextExperiment: "Before finalizing, ask for the strongest remaining concern and decide whether it changes the path or becomes a named follow-up.",
    encouragement: "You were clear about the need to decide. The next step is keeping that clarity connected to the concerns still in the room.",
    whyThisMatters: "When decision pressure is named explicitly, people can distinguish commitment from unresolved risk.",
    strengthsObserved: [
      {
        quote: "We need to choose the direction now.",
        what_worked: "You named the decision point directly.",
        impact: "The meeting moved out of ambiguity and toward commitment.",
      },
    ],
    growthEdges: [
      {
        quote: "We can return to that after the forecast is committed.",
        moment: "A concern surfaced as the decision was closing.",
        alternative_approach: "Acknowledge the concern and decide whether it is a blocker or a follow-up before commitment.",
        micro_action: "Ask, 'Does that concern change the decision, or should we name it as a follow-up risk?'",
      },
    ],
  },
  {
    id: "lt3",
    meetingTitle: "Stakeholder Update",
    occurredAt: "2026-07-11T16:00:00.000Z",
    scrubbedTranscript: [
      "00:16:02 Participant 1: We have two different interpretations of the customer signal.",
      "00:16:18 Leader: Let us slow down and hear the other perspectives before we decide.",
      "00:16:41 Participant 2: The renewal conversations point to a different constraint.",
      "00:17:06 Leader: Priya owns the follow-up, and we will return to it Friday.",
    ].join("\n"),
    analysisVersion: 1,
    overallAssessment: "You slowed the conversation down and paired perspective-taking with a clear follow-up owner.",
    summaryText: "The group had competing interpretations of the signal, and you kept the decision provisional while naming ownership for the next step.",
    keyInsight: "This is the clearest example of creating space and following through in the same meeting.",
    nextExperiment: "When evidence is mixed, name who owns the next check and when the group will revisit it.",
    encouragement: "This meeting shows a repeatable pattern: slow the room down, then make the follow-up visible.",
    whyThisMatters: "Perspective-taking only helps if the team also knows what happens next.",
    strengthsObserved: [
      {
        quote: "Let us slow down and hear the other perspectives before we decide.",
        what_worked: "You slowed the decision long enough for another interpretation to surface.",
        impact: "The team could compare customer signals before committing.",
      },
      {
        quote: "Priya owns the follow-up, and we will return to it Friday.",
        what_worked: "You named an owner and a return point.",
        impact: "The open question became a visible next step instead of lingering ambiguity.",
      },
    ],
    growthEdges: [
      {
        quote: "We have two different interpretations of the customer signal.",
        moment: "The team named competing reads of the same evidence.",
        alternative_approach: "Ask each side to identify the observation that would change its mind.",
        micro_action: "Say, 'What evidence would make each interpretation less likely?'",
      },
    ],
  },
  {
    id: "lt4",
    meetingTitle: "Product Review",
    occurredAt: "2026-07-15T16:00:00.000Z",
    scrubbedTranscript: [
      "00:11:03 Participant 1: We need a direction today.",
      "00:11:17 Leader: Let me reflect back the tradeoff I am hearing.",
      "00:11:31 Participant 2: Customer calls favor the simpler workflow.",
      "00:12:04 Leader: What would disconfirm the conclusion we are forming?",
      "00:12:27 Leader: We can decide once the remaining evidence is clear.",
    ].join("\n"),
    analysisVersion: 1,
    overallAssessment: "You reflected the tradeoff before naming a path, then tested the forming conclusion against disconfirming evidence.",
    summaryText: "The product review carried real decision pressure, but you kept the criteria visible and asked what could change the team's read.",
    keyInsight: "Testing a forming conclusion is a strong version of creating space because it protects the team from false certainty.",
    nextExperiment: "When the room leans toward a decision, ask for the disconfirming signal before closing.",
    encouragement: "You are turning evidence checks into a concrete team habit.",
    whyThisMatters: "A decision becomes easier to carry when people know what would cause the team to revisit it.",
    strengthsObserved: [
      {
        quote: "Let me reflect back the tradeoff I am hearing.",
        what_worked: "You reflected the tradeoff before moving to the decision.",
        impact: "The room could correct or sharpen the frame first.",
      },
      {
        quote: "What would disconfirm the conclusion we are forming?",
        what_worked: "You tested the team's confidence before closure.",
        impact: "The decision criteria became more explicit.",
      },
    ],
    growthEdges: [
      {
        quote: "We can decide once the remaining evidence is clear.",
        moment: "The team delayed commitment until the last evidence gap was named.",
        alternative_approach: "Name the specific evidence owner and decision threshold in the same sentence.",
        micro_action: "Say, 'If support confirms X by Friday, we will move forward; if not, we will revisit.'",
      },
    ],
  },
  {
    id: "lt5",
    meetingTitle: "Roadmap Commitment",
    occurredAt: "2026-07-18T16:00:00.000Z",
    scrubbedTranscript: [
      "00:22:08 Participant 1: The prior action is still open.",
      "00:22:24 Leader: This commitment connects to the decision we make at Friday's checkpoint.",
      "00:22:46 Participant 2: I will bring the final recommendation.",
    ].join("\n"),
    analysisVersion: 1,
    overallAssessment: "You connected an open commitment to the next decision point instead of letting it remain a loose action item.",
    summaryText: "The roadmap conversation closed with a visible commitment and a clear checkpoint for the final recommendation.",
    keyInsight: "Follow-through becomes stronger when commitments are tied to the decision they support.",
    nextExperiment: "When naming an owner, also name the decision or checkpoint their work will inform.",
    encouragement: "This is a practical way to make follow-through more visible without adding process overhead.",
    whyThisMatters: "Teams can carry commitments better when they know how the work affects the next decision.",
    strengthsObserved: [
      {
        quote: "This commitment connects to the decision we make at Friday's checkpoint.",
        what_worked: "You connected the commitment to a visible decision point.",
        impact: "The follow-up had a purpose beyond simple task completion.",
      },
    ],
    growthEdges: [
      {
        quote: "The prior action is still open.",
        moment: "The team surfaced an unresolved prior action.",
        alternative_approach: "Before moving on, ask whether the open action changes the current commitment.",
        micro_action: "Ask, 'Does this open action change what we are committing to now?'",
      },
    ],
  },
];

export function getPreviewTranscriptAnalysis(id: string | null | undefined) {
  if (!id) return null;
  return PREVIEW_TRANSCRIPT_ANALYSES.find((item) => item.id === id) ?? null;
}

// ─── Coach Veera Page ─────────────────────────────────────────────────────────

export const PREVIEW_VEERA_THREADS = [
  { id: "t1", title: "Draft a note to my coach", preview: "Sure! Here's a draft message you can use...", date: "Mar 30" },
];

export const PREVIEW_VEERA_CONVERSATION = [
  {
    id: "v1",
    role: "user" as const,
    content: "Draft a note to my coach",
  },
  {
    id: "v2",
    role: "assistant" as const,
    content: `Sure! Here's a draft message you can use to update your coach:\n\nSubject: Reflection on Recent Meetings\n\nHi Coach,\n\nI wanted to share my thoughts on the recent meetings we've had. During the discussions, I noticed that I tended to dominate the dialogue and focused a lot on technology and its applications. While I appreciate the collaborative spirit, I realized that I didn't always outline clear next steps for action items. I'm aiming to improve my clarity and ensure every participant knows their responsibilities moving forward.\n\nLooking forward to your guidance on this!\n\nBest,\nAlex\n\nWould you like me to adjust anything?`,
  },
];

export const PREVIEW_VEERA_THOUGHT_JOGGERS = [
  "Summarize my recent moments",
  "What should I do next?",
  "Find my next reflection",
];

// ─── Throughline Page ─────────────────────────────────────────────────────────

export const PREVIEW_THROUGHLINE = {
  behavioralObservation: {
    moment: "Strategic Listening is the clearest behavioral pattern currently showing in your throughline.",
    pattern: "This pattern is currently active across 4 meetings.",
    reflection: "One thing to notice: this pattern appears consistently in 1:1 contexts but less in group settings.",
  },
  // Pulse chart: simulated arc points
  arcPoints: [
    { id: "Mar 17", label: "Mar 17", state: "healthy" as const, evidence: 0.7, agreement: 0.8, momentum: 0.6, count: 2 },
    { id: "Mar 19", label: "Mar 19", state: "healthy" as const, evidence: 0.85, agreement: 0.9, momentum: 0.75, count: 3 },
    { id: "Mar 22", label: "Mar 22", state: "stalled" as const, evidence: 0.4, agreement: 0.5, momentum: 0.35, count: 1 },
    { id: "Mar 24", label: "Mar 24", state: "healthy" as const, evidence: 0.9, agreement: 0.88, momentum: 0.82, count: 4 },
    { id: "Mar 26", label: "Mar 26", state: "healthy" as const, evidence: 0.78, agreement: 0.82, momentum: 0.71, count: 2 },
    { id: "Mar 28", label: "Mar 28", state: "healthy" as const, evidence: 0.82, agreement: 0.76, momentum: 0.80, count: 3 },
    { id: "Mar 30", label: "Mar 30", state: "healthy" as const, evidence: 0.88, agreement: 0.85, momentum: 0.84, count: 3 },
  ],
  selectedDay: {
    label: "Mar 30, 2026",
    state: "healthy" as const,
    confidence: "high" as const,
    metaBehaviorHeadline: "Strategic Listening showed up in reflection and transcript evidence on this day.",
    topBehaviors: ["Strategic Listening", "Clarifying Questions"],
    summary: "You paused before responding and asked follow-up questions in the Product Roadmap Sync. This is consistent with the broader pattern Veera has observed this week.",
    confidenceReason: "Two independent sources (transcript + reflection) support this signal with high agreement.",
    count: 3,
    evidence: 0.88,
    agreement: 0.85,
    momentum: 0.84,
    alignment: "aligned" as const,
    drivers: [
      "Transcript analysis matched the behavioral intent you set at the start of the week.",
      "Your post-meeting reflection confirmed the moment with specific detail.",
    ],
    sourceCounts: { transcript: 2, reflection: 1, conversation: 0, veera: 1 },
    itemsByType: {
      transcript: [
        { id: "ts1", type: "transcript" as const, at: "2026-03-30T10:45:00Z", detail: "You asked 3 clarifying questions before offering a solution in the Product Roadmap Sync.", link: null },
      ],
      reflection: [
        { id: "r1", type: "reflection" as const, at: "2026-03-30T13:15:00Z", detail: "Reflected on slowing down in the roadmap meeting. Felt like the team appreciated the space.", link: null },
      ],
      conversation: [],
      veera: [
        { id: "ve1", type: "veera" as const, at: "2026-03-30T17:00:00Z", detail: "Veera noted this meeting as supporting your Strategic Listening pattern.", link: null },
      ],
    },
  },
  // Continuity summary
  continuity: {
    workedOn: ["Helping Alex slow down in group discussions before offering opinions.", "Practicing strategic listening in upcoming 1:1 sessions."],
    direction: ["Continue applying Strategic Listening in roadmap meetings.", "Aim to summarize what you heard before sharing a perspective."],
    nextSignals: ["Check if the pattern holds in the Leadership Forum on Thursday.", "Bring a specific moment from this week to the next coaching call."],
  },
  // Evidence
  evidenceStats: ["4 meetings included", "3 transcripts analyzed", "2 reflections saved"],
  transcriptSignals: [
    {
      id: "sig1",
      behaviorName: "Strategic Listening",
      meetingTitle: "Product Roadmap Sync",
      meetingDate: "Mar 30, 2026",
      practiceTitle: "Pause and ask first",
      transcriptSummary: "You asked 3 clarifying questions before offering your perspective. The team appeared more engaged as a result.",
      practiceInstruction: "Before sharing your opinion, summarize what you heard and confirm understanding.",
    },
    {
      id: "sig2",
      behaviorName: "Strategic Listening",
      meetingTitle: "1:1 with Sarah Chen",
      meetingDate: "Mar 28, 2026",
      practiceTitle: "Hold space",
      transcriptSummary: "You allowed Sarah to finish her train of thought without interrupting. She shared a concern she hadn't raised before.",
      practiceInstruction: "Resist the urge to fix; ask what would help most right now.",
    },
  ],
};

// ─── Coach Messages Page ──────────────────────────────────────────────────────

export const PREVIEW_COACH_MESSAGES = {
  coachName: PREVIEW_COACH_NAME,
  coachInitials: "JL",
  intentText: "To practice Strategic Listening by pausing before responding and asking at least one clarifying question in each meeting this week.",
  intentBehaviorName: PREVIEW_BEHAVIOR_NAME,
  intentWeek: 4,
  cadenceCopy: "Weekly review + anytime messaging + monthly call",
  behavioralObservation: {
    headline: "Your coaching thread is currently anchored in Strategic Listening, and the next conversation already has a clear landing point in your Leadership Forum meeting.",
    detail: "The active practice intent is still the clearest thread to carry into your next coach exchange.",
  },
  upcomingSessions: [
    { id: "s1", title: "Weekly Coaching Call", scheduledStart: "2026-04-02T14:00:00Z", meetingLink: null },
    { id: "s2", title: "Leadership Forum Prep", scheduledStart: "2026-04-07T10:00:00Z", meetingLink: null },
  ],
  messages: [
    {id: "preview-shared-document", senderId: "coach", content: "A few questions to help you notice what happens when you pause. Bring one example to our next session.", createdAt: "2026-10-28T16:00:00Z"},
    {
      id: "msg1",
      senderId: "coach",
      content: "Hi Alex — great work this week on the Stakeholder Update. I noticed from your reflection that you're already starting to hold space before responding. That's a real shift.",
      createdAt: "2026-03-24T09:12:00Z",
    },
    {
      id: "msg2",
      senderId: "leader",
      content: "Thanks Jordan! It felt different in that meeting — I caught myself about to jump in and just... waited. The team seemed to fill the silence in a useful way.",
      createdAt: "2026-03-24T10:05:00Z",
    },
    {
      id: "msg3",
      senderId: "coach",
      content: "That's exactly it. The pause is the practice. Before our next call, bring one specific moment from this week where you asked a clarifying question and noticed the effect.",
      createdAt: "2026-03-24T10:22:00Z",
    },
    {
      id: "msg4",
      senderId: "leader",
      content: "Will do. The Product Roadmap Sync on Monday had a clear moment — I'll write it up.",
      createdAt: "2026-03-25T08:45:00Z",
    },
    {
      id: "msg5",
      senderId: "coach",
      content: "Perfect. See you Wednesday.",
      createdAt: "2026-03-25T09:01:00Z",
    },
  ],
};

// Fictional, approved material for the same My Coach components used by leaders.
export const PREVIEW_SHARED_AGREEMENTS = [{
  id: 'preview-agreement-1',
  createdAt: '2026-10-27T16:00:00Z',
  updatedAt: '2026-10-28T16:00:00Z',
  discussed: [
    'You noticed that pausing before offering your view gave the team room to explain its reasoning.',
    'Time pressure made it harder to ask the follow-up question you wanted to ask.',
  ],
  nextFocus: ['In your next team discussion, ask one clarifying question before offering your recommendation.'],
  nextSession: ['Bring a specific moment when you paused, and what happened after your question.'],
  actionItems: ['Write a short note after the discussion while the moment is fresh.'],
  reflectionPrompts: ['What helped you stay curious when you felt pressure to decide?'],
}];
